/**
 * AI 주가 분석 서비스
 * Google Gemini API를 사용한 주가 변동 원인 분석
 */
import api from './api';

const aiAnalysisService = {
  /**
   * AI 주가 변동 분석 요청
   * @param {Object} stockData - 종목 데이터
   * @param {string} stockData.symbol - 종목 심볼
   * @param {string} stockData.stock_name - 종목명
   * @param {number} stockData.current_price - 현재가
   * @param {number} stockData.prev_day_change - 전일대비 변화량
   * @param {number} stockData.prev_day_change_percent - 전일대비 변화율
   * @param {string} stockData.date - 분석 날짜 (optional)
   * @param {string} stockData.custom_prompt - 사용자 정의 prompt (optional)
   * @returns {Promise} API 응답
   */
  analyzeStockMovement: async (stockData) => {
    try {
      // console.log('🤖 AI 주가 변동 분석 요청:', stockData);
      
      const payload = {
        symbol: stockData.symbol,
        stock_name: stockData.stock_name,
        current_price: stockData.current_price,
        prev_day_change: stockData.prev_day_change,
        prev_day_change_percent: stockData.prev_day_change_percent,
        date: stockData.date || new Date().toISOString().split('T')[0]
      };
      
      // custom_prompt가 있으면 추가
      if (stockData.custom_prompt) {
        payload.custom_prompt = stockData.custom_prompt;
      }
      
      // analysis_type이 있으면 추가 (market: 시황 분석, company: 기업 분석)
      if (stockData.analysis_type) {
        payload.analysis_type = stockData.analysis_type;
      }
      
      const response = await api.post('/api/data_predator/stock-analysis/', payload);
      
      // console.log('✅ AI 주가 변동 분석 성공:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ AI 주가 변동 분석 실패:', error);
      throw error;
    }
  },

  /**
   * 분석 캐시 삭제
   * @param {Object} params - 삭제 파라미터
   * @param {string} params.symbol - 종목 심볼 (optional)
   * @param {string} params.date - 날짜 (optional)
   * @returns {Promise} API 응답
   */
  clearAnalysisCache: async (params = {}) => {
    try {
      const response = await api.delete('/api/data_predator/stock-analysis/cache/', {
        data: params
      });
      return response.data;
    } catch (error) {
      console.error('Failed to clear analysis cache', error);
      throw error;
    }
  },

  /**
   * 분석 히스토리 목록 조회
   * @param {Object} options - 조회 옵션
   * @param {number} options.stockId - 종목 ID (optional)
   * @param {string} options.symbol - 종목 심볼 (optional)
   * @param {number} options.limit - 조회 개수 (default: 10)
   * @returns {Promise} API 응답
   */
  getAnalysisHistory: async (options = {}) => {
    try {
      const { stockId, symbol, limit = 10 } = options;
      const params = { limit };
      
      if (stockId) {
        params.stock_id = stockId;
      } else if (symbol) {
        params.symbol = symbol;
      }
      
      const response = await api.get('/api/data_predator/stock-analysis/history/', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analysis history', error);
      throw error;
    }
  },

  /**
   * 특정 분석 히스토리 상세 조회
   * @param {number} analysisId - 분석 ID
   * @returns {Promise} API 응답
   */
  getAnalysisDetail: async (analysisId) => {
    try {
      const response = await api.get(`/api/data_predator/stock-analysis/history/${analysisId}/`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analysis detail', error);
      throw error;
    }
  },

  /**
   * 분석 히스토리 삭제
   * @param {number} analysisId - 분석 ID
   * @returns {Promise} API 응답
   */
  deleteAnalysisHistory: async (analysisId) => {
    try {
      const response = await api.delete(`/api/data_predator/stock-analysis/history/${analysisId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete analysis history', error);
      throw error;
    }
  }
};

export default aiAnalysisService;
